import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Typography, CircularProgress, Avatar } from '@mui/material';
import './CoachsList.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfessionalService } from '../../services/ProfessionalService';
import { checkImageURL } from '../../utils/validations';
import { getImageSrc } from '../../utils/imageUtils';
import image_not_found from '../../assets/image_not_found.png';
import { UserProfilePicture } from '../../services/UserProfilePicture';

// Professional interface for UI
interface Professional {
  id: number;
  name: string;
  specialty: string;
  description: string;
  image: string | null;
  languages: string[];
  location: string;
  price: string;
  specialties: string[];
  availability: {
    dates: number[];
    timeSlots: string[];
  };
}

const CoachsList: React.FC = () => {
  const location = useLocation();
  const specialtyTitle = location.state?.specialtyTitle;
  const [specialists, setSpecialists] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profilePictures, setProfilePictures] = useState<{ [key: number]: string | null }>({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    ProfessionalService.getAllCoachs()
      .then(async (data) => {
        const mapped = (Array.isArray(data) ? data : []).map((item: any) => {
          const prof = item.professionnel || {};
          const user = item.utilisateur || {};
          // Prefer user.id, fallback to prof.utilisateur_id
          const utilisateur_id = user.id || prof.utilisateur_id;
          // Debug log
          console.log('Mapping professional:', { prof, user, utilisateur_id });
          return {
            id: prof.id,
            utilisateur_id,
            name: `${user.prenom || ''} ${user.nom || ''}`.trim(),
            specialty: (prof.specialites && prof.specialites[0]) || 'Beauty Coaching',
            description: prof.sous_specialites || 'Beauty coach',
            image: prof.photo_url || null,
            languages: prof.langues && prof.langues.length > 0 ? prof.langues : ['Arabic', 'French'],
            location: (prof.pays_exercice && prof.pays_exercice[0]) || 'Tunisia',
            price: prof.honoraire_montant && prof.honoraire_montant > 0 ? `${prof.honoraire_montant} ${prof.honoraire_devise || 'TND'}` : 'N/A',
            specialties: prof.specialites || ['Beauty Coaching'],
            availability: {
              dates: prof.availability?.dates || [],
              timeSlots: prof.availability?.timeSlots || [],
            },
          };
        });
        // Fetch profile pictures in parallel
        const pics: { [key: number]: string | null } = {};
        await Promise.all(
          mapped.map(async (spec) => {
            if (spec.utilisateur_id) {
              try {
                const res = await UserProfilePicture.getProfilePicture(spec.utilisateur_id);
                pics[spec.id] = res?.image_base64 || null;
              } catch {
                pics[spec.id] = null;
              }
            } else {
              pics[spec.id] = null;
            }
          })
        );
        setProfilePictures(pics);
        setSpecialists(mapped);
        setLoading(false);
      })
      .catch((err) => {
        setError('Failed to load coaches.');
        setLoading(false);
      });
  }, [specialtyTitle]);

  return (
    <div className="specialist-list-page">
      <div className="container">
        <div className="section-header">
          <Typography variant="h2" className="section-title">
            Our beauty coaches
          </Typography>
          <Typography className="section-description">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
            dolore magna aliqua. Ut enim ad minim veniam
          </Typography>
        </div>
        {loading ? (
          <div className="specialist-loading"><CircularProgress /></div>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <div className="specialist-grid">
            {specialists.length === 0 ? (
              <div
                className="no-specialists-card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(245, 247, 250, 0.95)',
                  borderRadius: 20,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  padding: 40,
                  margin: '40px auto',
                  maxWidth: 420,
                  minHeight: 340,
                }}
              >
                {/* Illustration: person SVG avatar */}
                <span style={{marginBottom: 24}}>
                  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="36" cy="36" r="36" fill="#e0e7ef" />
                    <circle cx="36" cy="30" r="14" fill="#b0c4de" />
                    <ellipse cx="36" cy="54" rx="20" ry="12" fill="#b0c4de" />
                    <ellipse cx="36" cy="54" rx="16" ry="9" fill="#e0e7ef" />
                    <path d="M36 44c-7 0-14 3-14 7v2c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2v-2c0-4-7-7-14-7z" fill="#b0c4de" />
                    <circle cx="36" cy="30" r="8" fill="#e0e7ef" />
                    <ellipse cx="36" cy="38" rx="6" ry="4" fill="#e0e7ef" />
                  </svg>
                </span>
                <Typography variant="h5" className="no-specialists-title" style={{marginBottom: 12, color: '#2D3A4A', fontWeight: 700}}>
                  No coaches available yet
                </Typography>
                <Typography className="no-specialists-message" style={{marginBottom: 24, color: '#5A6A85', textAlign: 'center', fontSize: 17}}>
                  We're working to bring top beauty coaches soon. Please check back later!
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  style={{borderRadius: 8, padding: '10px 28px', fontWeight: 600, fontSize: 16}}
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>
              </div>
            ) : (
              specialists.map((specialist) => (
                <SpecialistCard key={specialist.id} specialist={specialist} profilePictures={profilePictures} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SpecialistCard = ({ specialist, profilePictures }: { specialist: Professional & { utilisateur_id?: number }, profilePictures: { [key: number]: string | null } }) => {
  const navigate = useNavigate();

  // Always pass utilisateur_id in navigation state for DoctorBooking
  const handleBookConsultationClick = () => {
    navigate(`/beautycoaching/booking`, { state: { coach: { ...specialist, utilisateur_id: specialist.utilisateur_id } } });
  };

  // Helper to get initials from name
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0][0] || '';
    return (parts[0][0] || '') + (parts[1][0] || '');
  };

  return (
    <Card className="specialist-card" style={{ width: '100%' }}>
      <CardContent className="specialist-card-content">
        {profilePictures[specialist.id] ? (
          <img
            className="specialist-image"
            alt={specialist.name}
            src={profilePictures[specialist.id] || undefined}
          />
        ) : (
          <span style={{marginBottom: 20, display: 'block'}}>
            <svg width="240" height="240" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="36" cy="36" r="36" fill="#e0e7ef" />
              <circle cx="36" cy="30" r="14" fill="#b0c4de" />
              <ellipse cx="36" cy="54" rx="20" ry="12" fill="#b0c4de" />
              <ellipse cx="36" cy="54" rx="16" ry="9" fill="#e0e7ef" />
              <path d="M36 44c-7 0-14 3-14 7v2c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2v-2c0-4-7-7-14-7z" fill="#b0c4de" />
              <circle cx="36" cy="30" r="8" fill="#e0e7ef" />
              <ellipse cx="36" cy="38" rx="6" ry="4" fill="#e0e7ef" />
            </svg>
          </span>
        )}
        <Typography variant="h3" className="specialist-name">
          {specialist.name}
        </Typography>
        <Typography className="specialist-fee">
          {specialist.price}
        </Typography>
        <Typography className="specialist-description">
          {specialist.description}
        </Typography>
        <Button className="book-button" onClick={handleBookConsultationClick}>
          <Typography className="book-button-text">
            Book a session
          </Typography>
          <span className="arrow-icon">&gt;</span>
        </Button>
      </CardContent>
    </Card>
  );
};

export default CoachsList;


